import { billetPaid, billetReceived, cardReceivable, judicialBlock, judicialUnblock, pixReceived, pixReversalIn, pixReversalOut, pixSent, pixUnderReview, rechargeTransaction, transferReceived, transferSent, } from '@ibk/shared-assets';
import { useTranslation } from '@ibk/shared-translation';
import { formatCurrency, formatLongDayAndMonthOrToday } from '@ibk/shared-utils';
import { ScheduledTransactionType, TransactionFlow, TransactionType, TransactionTypeFlow } from '../../enums';
const SAO_PAULO_TIME_ZONE = 'America/Sao_Paulo';
function getSaoPauloDateKey(date) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: SAO_PAULO_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date(date));
}
function getPixUnderReviewData(transaction) {
    if (transaction.flow === TransactionFlow.CASH_OUT) {
        return {
            image: pixUnderReview,
            title: 'pix-sent-under-review',
            typeFlow: TransactionTypeFlow.PIX_SENT,
            description: transaction.recipientName,
        };
    }
    return {
        image: pixUnderReview,
        title: 'pix-received-under-review',
        typeFlow: TransactionTypeFlow.PIX_RECEIVED,
        description: transaction.payerName,
    };
}
function getDateIndex(groupKey, date, extract, indexByGroup, balance) {
    const existingIndex = indexByGroup.get(groupKey);
    if (existingIndex !== undefined) {
        if (balance !== undefined) {
            extract[existingIndex].balance = balance;
        }
        return existingIndex;
    }
    const dateIndex = extract.push({ date, balance, data: [] }) - 1;
    indexByGroup.set(groupKey, dateIndex);
    return dateIndex;
}
function getData(transaction) {
    if (transaction.type === TransactionType.PIX &&
        transaction.status === 'PENDING' &&
        (transaction.flow === TransactionFlow.CASH_IN || transaction.flow === TransactionFlow.CASH_OUT)) {
        return getPixUnderReviewData(transaction);
    }
    const data = {
        [`${TransactionType.PIX} - ${TransactionFlow.CASH_IN}`]: {
            image: pixReceived,
            title: 'pix-received',
            typeFlow: TransactionTypeFlow.PIX_RECEIVED,
            description: transaction.payerName,
        },
        [`${TransactionType.PIX} - ${TransactionFlow.CASH_OUT}`]: {
            image: pixSent,
            title: 'pix-sent',
            typeFlow: TransactionTypeFlow.PIX_SENT,
            description: transaction.recipientName,
        },
        [`${TransactionType.PIX} - ${TransactionFlow.REVERSAL_IN}`]: {
            image: pixReversalIn,
            title: 'reversal-pix',
            typeFlow: TransactionTypeFlow.PIX_REVERSAL_IN,
            description: transaction.recipientName,
        },
        [`${TransactionType.PIX} - ${TransactionFlow.REVERSAL_OUT}`]: {
            image: pixReversalOut,
            title: 'reversal-pix',
            typeFlow: TransactionTypeFlow.PIX_REVERSAL_OUT,
            description: transaction.payerName,
        },
        [`${TransactionType.PIX} - ${TransactionFlow.SCHEDULED}`]: {
            image: pixSent,
            title: 'pix-scheduled',
            typeFlow: TransactionTypeFlow.PIX_SCHEDULED,
            description: transaction.recipientName,
        },
        [`${TransactionType.BILLET} - ${TransactionFlow.CASH_IN}`]: {
            image: billetReceived,
            title: 'billet-received',
            typeFlow: TransactionTypeFlow.BILLET_RECEIVED,
            description: transaction.payerName,
        },
        [`${TransactionType.BILLET} - ${TransactionFlow.CASH_OUT}`]: {
            image: billetPaid,
            title: 'billet-paid',
            typeFlow: TransactionTypeFlow.BILLET_PAID,
            description: transaction.recipientName,
        },
        [`${TransactionType.BILLET} - ${TransactionFlow.REVERSAL_IN}`]: {
            image: billetReceived,
            title: 'billet-reversal',
            typeFlow: TransactionTypeFlow.BILLET_REVERSAL,
            description: transaction.recipientName,
        },
        [`${TransactionType.BILLET} - ${TransactionFlow.SCHEDULED}`]: {
            image: billetPaid,
            title: 'billet-scheduled',
            typeFlow: TransactionTypeFlow.BILLET_SCHEDULED,
            description: transaction.recipientName,
        },
        [`${TransactionType.TED} - ${TransactionFlow.CASH_IN}`]: {
            image: transferReceived,
            title: 'ted',
            typeFlow: TransactionTypeFlow.TRANSFER_RECEIVED,
            description: transaction.payerName,
        },
        [`${TransactionType.TED} - ${TransactionFlow.CASH_OUT}`]: {
            image: transferSent,
            title: 'ted',
            typeFlow: TransactionTypeFlow.TRANSFER_SENT,
            description: transaction.recipientName,
        },
        [`${TransactionType.TED} - ${TransactionFlow.REVERSAL_IN}`]: {
            image: transferReceived,
            title: 'ted-reversal',
            typeFlow: TransactionTypeFlow.TRANSFER_REVERSAL,
            description: transaction.recipientName,
        },
        [`${TransactionType.P2P} - ${TransactionFlow.CASH_IN}`]: {
            image: transferReceived,
            title: 'transfer-between-accounts',
            typeFlow: TransactionTypeFlow.P2P_RECEIVED,
            description: transaction.payerName,
        },
        [`${TransactionType.P2P} - ${TransactionFlow.CASH_OUT}`]: {
            image: transferSent,
            title: 'transfer-between-accounts',
            typeFlow: TransactionTypeFlow.P2P_SENT,
            description: transaction.recipientName,
        },
        [`${TransactionType.P2P} - ${TransactionFlow.REVERSAL_IN}`]: {
            image: transferReceived,
            title: 'transfer-between-accounts-reversal',
            typeFlow: TransactionTypeFlow.P2P_REVERSAL,
            description: transaction.recipientName,
        },
        [`${TransactionType.RECHARGE_BILLET} - ${TransactionFlow.CASH_IN}`]: {
            image: rechargeTransaction,
            title: 'billet-recharge',
            typeFlow: TransactionTypeFlow.RECHARGE,
            description: transaction.payerName,
        },
        [`${TransactionType.BALANCE_BLOCK} - ${TransactionFlow.CASH_OUT}`]: {
            image: judicialBlock,
            title: 'judicial-block',
            typeFlow: TransactionTypeFlow.BALANCE_BLOCK,
            description: transaction.description,
        },
        [`${TransactionType.BALANCE_UNLOCK} - ${TransactionFlow.CASH_IN}`]: {
            image: judicialUnblock,
            title: 'judicial-unblock',
            typeFlow: TransactionTypeFlow.BALANCE_UNLOCK,
            description: transaction.description,
        },
        [`${TransactionType.CARD_RECEIVABLE} - ${TransactionFlow.CASH_IN}`]: {
            image: cardReceivable,
            title: 'card-receivable',
            typeFlow: TransactionTypeFlow.CARD_RECEIVABLE,
            description: '',
        },
    };
    return (data[`${transaction.type} - ${transaction.flow}`] ?? {
        image: rechargeTransaction,
        title: 'another',
        typeFlow: TransactionTypeFlow.ANOTHER,
        description: transaction.description,
    });
}
function toTransactions(scheduledTransactions) {
    const transactionTypes = {
        [ScheduledTransactionType.BANK_SLIP]: TransactionType.BILLET,
        [ScheduledTransactionType.PIX]: TransactionType.PIX,
    };
    return scheduledTransactions.map(scheduledTransaction => ({
        transactionId: scheduledTransaction.id,
        amount: scheduledTransaction.amount,
        time: scheduledTransaction.date,
        description: scheduledTransaction.title,
        recipientName: scheduledTransaction.receiverName,
        payerName: scheduledTransaction.receiverName,
        type: transactionTypes[scheduledTransaction.type],
        flow: TransactionFlow.SCHEDULED,
        title: scheduledTransaction.title,
    }));
}
export function useFormatExtract() {
    const { t, currentLanguage } = useTranslation();
    function toExtract(transactions, dailyBalances) {
        const extract = [];
        const indexByGroup = new Map();
        [...transactions]
            .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
            .forEach(transaction => {
            const formattedDate = formatLongDayAndMonthOrToday(transaction.time, currentLanguage);
            const dateKey = getSaoPauloDateKey(transaction.time);
            const balanceOfTheDay = dailyBalances?.[dateKey];
            const groupKey = `${new Date(transaction.time).getFullYear()}|${formattedDate}`;
            const dateIndex = getDateIndex(groupKey, formattedDate, extract, indexByGroup, balanceOfTheDay);
            const { image, title, typeFlow, description } = getData(transaction);
            const translatedTitle = t(title);
            const finalTitle = typeFlow === TransactionTypeFlow.CARD_RECEIVABLE && transaction.paymentArrangement
                ? `${translatedTitle} - ${transaction.paymentArrangement}`
                : translatedTitle;
            extract[dateIndex].data.push({
                id: transaction.transactionId,
                coreBankId: transaction.coreBankId,
                authorizationId: transaction.authorizationId,
                amount: formatCurrency(transaction.amount),
                title: finalTitle,
                description: description ?? transaction.description,
                image: image,
                typeFlow: typeFlow,
                type: transaction.type,
                flow: transaction.flow,
                endToEndId: transaction.endToEndId,
                status: transaction.status,
                paymentArrangement: transaction.paymentArrangement,
            });
        });
        return extract;
    }
    function toScheduledExtract(scheduledTransactions) {
        return toExtract(toTransactions(scheduledTransactions));
    }
    return { toExtract, toScheduledExtract };
}
